import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Dialog } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { motion, AnimatePresence } from 'framer-motion';
import './ProjectDetail.css';

const ProjectDetail = ({ project, open, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Reset image index when a different project is opened
  useEffect(() => {
    if (open) {
      setCurrentImageIndex(0);
    }
  }, [project, open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open || !project) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        setCurrentImageIndex((prev) => (prev === project.gallery.length - 1 ? 0 : prev + 1));
      } else if (e.key === 'ArrowLeft') {
        setCurrentImageIndex((prev) => (prev === 0 ? project.gallery.length - 1 : prev - 1));
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, project, onClose]);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  if (!project) return null;

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? project.gallery.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === project.gallery.length - 1 ? 0 : prev + 1));
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNextImage();
    }
    if (isRightSwipe) {
      handlePrevImage();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: { xs: '100%', sm: '1600px' },
          width: { xs: '100%', sm: '95vw' },
          height: { xs: '100%', sm: '95vh' },
          maxHeight: { xs: '100%', sm: '95vh' },
          backgroundColor: '#fff',
          borderRadius: 0,
          m: { xs: 0, sm: 1, md: 2 }
        }
      }}>
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          zIndex: 10,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          color: '#fff',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}>
        <CloseIcon />
      </IconButton>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}>
        {/* Top - Images */}
        <Box
          sx={{
            flex: currentImageIndex === 0 ? '0 0 65%' : '1',
            position: 'relative',
            backgroundColor: '#1a1a1a',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'flex 0.3s ease'
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}>
          {/* Main Image */}
          <AnimatePresence mode='wait'>
            <motion.img
              key={currentImageIndex}
              src={project.gallery[currentImageIndex]}
              alt={`${project.title} - ${currentImageIndex + 1}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                display: 'block'
              }}
            />
          </AnimatePresence>

          {/* Navigation Arrows */}
          {project.gallery.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: '#fff',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    border: '2px solid rgba(255, 255, 255, 0.6)'
                  }
                }}>
                <ArrowBackIosNewIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: '#fff',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    border: '2px solid rgba(255, 255, 255, 0.6)'
                  }
                }}>
                <ArrowForwardIosIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </>
          )}

          {/* Image Counter */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: '#fff',
              px: 2,
              py: 0.5,
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}>
            {currentImageIndex + 1} / {project.gallery.length}
          </Box>

          {/* Thumbnail Strip */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 50,
              left: '50%',
              transform: 'translateX(-50%)',
              display: { xs: 'none', sm: 'flex' },
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'center',
              maxWidth: '90%'
            }}>
            {project.gallery.map((img, index) => (
              <Box
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                sx={{
                  width: 60,
                  height: 60,
                  backgroundImage: `url(${img})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  cursor: 'pointer',
                  border: index === currentImageIndex ? '3px solid #fff' : '3px solid transparent',
                  opacity: index === currentImageIndex ? 1 : 0.6,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.05)'
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Bottom - Project Info (only on first image) */}
        {currentImageIndex === 0 && (
          <Box
            sx={{
              flex: '0 0 35%',
              p: { xs: 2.5, sm: 3, md: 4 },
              overflowY: 'auto',
              backgroundColor: '#f9f9f9',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 4 },
              alignItems: { md: 'flex-start' }
            }}>
            {/* Left info column */}
            <Box sx={{ flex: { md: '0 0 40%' } }}>
              <Typography
                variant='h4'
                sx={{
                  fontFamily: 'Jura, sans-serif',
                  fontWeight: 700,
                  mb: 1,
                  fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' },
                  letterSpacing: '0.05em'
                }}>
                {project.title}
              </Typography>

              <Typography
                variant='h6'
                sx={{
                  fontFamily: 'Jura, sans-serif',
                  color: '#666',
                  mb: 2,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  letterSpacing: '0.03em'
                }}>
                {project.location}
              </Typography>

              <Typography
                variant='body2'
                sx={{
                  color: '#999',
                  fontSize: '0.8rem',
                  letterSpacing: '0.05em',
                  mb: 1,
                  textTransform: 'uppercase'
                }}>
                Projektdetails
              </Typography>
              {project.details.split('|').map((detail, i) => {
                const parts = detail.trim().split(':');
                const label = parts[0].trim();
                const value = parts.slice(1).join(':').trim();
                return (
                  <Typography
                    key={i}
                    variant='body1'
                    sx={{
                      fontFamily: 'Jura, sans-serif',
                      color: '#333',
                      fontSize: '0.9rem',
                      lineHeight: 1.8
                    }}>
                    <strong>{label}:</strong> {value}
                  </Typography>
                );
              })}
            </Box>

            {/* Right description column */}
            <Box sx={{ flex: { md: '1' } }}>
              <Typography
                variant='body2'
                sx={{
                  color: '#999',
                  fontSize: '0.8rem',
                  letterSpacing: '0.05em',
                  mb: 0.5,
                  textTransform: 'uppercase'
                }}>
                Beschreibung
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  fontFamily: 'Jura, sans-serif',
                  color: '#333',
                  fontSize: '0.9rem',
                  lineHeight: 1.8
                }}>
                {project.description}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default ProjectDetail;
